import {
  DedicatedCategory,
  DedicatedRelation,
} from "@timeedit/registration-schemas";
import { isDefined, translations } from "@timeedit/registration-shared";
import { uniq } from "lodash-es";
import { useEffect, useMemo } from "react";
import {
  AllocationControlContext,
  IdContext,
  LoadActionContext,
  RegistrationContext,
  UtilContext,
} from "./RegistrationContexts";
import {
  RegistrationOptionsContext,
  defaultRegistrationSlots,
  makeHeadingLevel,
} from "./RegistrationOptions";
import { RegistrationDesktop } from "./RegistrationDesktop";
import { RegistrationMobile } from "./RegistrationMobile";
import { RegistrationProps } from "./types";
import "../../utils/dayjsSetup";

/**
 * @param mapping - The mapping
 * @param registration - Everything fetched from registration backend, includes all that necessary for the registration page
 * @param signal - The signal data used to display the signal summary
 * @param allocationControlActions - What happens when user press the different allocation control buttons
 * @param slots - Overrides for the certain parts of the Registration UI
 * @param isDesktop - Whether to use the desktop layout
 * @param listOnly - Hide the calendar view and its toggle
 * @param baseHeaderLevel - Level of the heading labelling the registration content; inner headings step down from it
 * @returns The registration page
 */

export default function RegistrationMain({
  studentId,
  mapping,
  registration,
  signal,
  isHub,
  allocationControlActions,
  mode = "student",
  navigate,
  dedicatedGroupsActions,
  loadActions,
  params,
  analyticsEvent,
  slots,
  isDesktop,
  listOnly = false,
  baseHeaderLevel = 1,
}: RegistrationProps): React.JSX.Element {
  const { id } = params;

  const defaultId = useMemo(() => {
    const defaultId =
      registration.courses[registration.courseIds?.[0]]?.children?.[0];
    return isDesktop ? defaultId : undefined;
  }, [isDesktop, registration.courses, registration.courseIds]);

  const registrationContext = useMemo(
    () => ({
      registration,
      mode,
    }),
    [registration, mode]
  );

  const idContext = useMemo(() => {
    let activityId: number | undefined = undefined;
    if (isDefined(id)) {
      activityId = Number(id);
    } else if (isDefined(defaultId)) {
      activityId = defaultId;
    }
    return {
      id: activityId,
      studentId,
    };
  }, [defaultId, id, studentId]);

  // Update URL with defaultId (only for desktop) and no id is in URL
  useEffect(() => {
    if (!isDefined(id) && isDefined(defaultId)) {
      navigate(`${defaultId}`, { replace: true });
    }
  }, [id, defaultId, navigate]);

  const utilContext = useMemo(
    () => ({
      navigate,
      translations,
      mapping,
      analyticsEvent,
      dedicatedGroupsActions: isDefined(dedicatedGroupsActions)
        ? dedicatedGroupsActions
        : {
            presentCategory: (data: DedicatedCategory[]) =>
              "Category: " +
              data
                .reduce((result: string[], it) => {
                  const alreadyExists = result.some((r) => r === it.value);
                  if (alreadyExists) return result;
                  return [...result, it.value];
                }, [])
                .join(", "),
            presentRelation: (data: DedicatedRelation[]) =>
              "Relation: " + uniq(data.map((d) => d.id)).join(", "),
          },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const options = useMemo(
    () => ({
      slots: {
        groupControls:
          slots?.groupControls ?? defaultRegistrationSlots.groupControls,
        activityBadge:
          slots?.activityBadge ?? defaultRegistrationSlots.activityBadge,
        activityDescription:
          slots?.activityDescription ??
          defaultRegistrationSlots.activityDescription,
      },
      isDesktop,
      listOnly,
      headingLevel: makeHeadingLevel(baseHeaderLevel),
    }),
    [
      slots?.groupControls,
      slots?.activityBadge,
      slots?.activityDescription,
      isDesktop,
      listOnly,
      baseHeaderLevel,
    ]
  );

  // This is needed because of custom views for the calendar. We need to pass
  // some props to the list view, and have not found a good way to pass props
  // directly to it beyond initial values. We need one context per thing that
  // should change together for performance reasons.
  //
  // We should resist using the context for any lower level components that we
  // need to be reusable for other things.
  return (
    <RegistrationContext.Provider value={registrationContext}>
      <IdContext.Provider value={idContext}>
        <AllocationControlContext.Provider value={allocationControlActions}>
          <LoadActionContext.Provider value={loadActions}>
            <UtilContext.Provider value={utilContext}>
              <RegistrationOptionsContext.Provider value={options}>
                {isDesktop ? (
                  <RegistrationDesktop signal={signal} isHub={isHub} />
                ) : (
                  <RegistrationMobile signal={signal} isHub={isHub} />
                )}
              </RegistrationOptionsContext.Provider>
            </UtilContext.Provider>
          </LoadActionContext.Provider>
        </AllocationControlContext.Provider>
      </IdContext.Provider>
    </RegistrationContext.Provider>
  );
}
